import { Field } from '@/components/ui/field';
import { Box, Button, Text, Textarea, VStack } from '@chakra-ui/react';
import type {
  DisplayRightsCompositionContextResponse,
  MentionBoostAuthorizationScope,
} from '@fluenceur/types';
import { Controller, type UseFormReturn } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { DisplayRightsChannelSection } from './DisplayRightsChannelSection';
import { DisplayRightsTermsFields } from './DisplayRightsTermsFields';
import type { DisplayRightsComposerValues } from './display-rights-composer-schema';

interface DisplayRightsComposerFormProps {
  form: UseFormReturn<DisplayRightsComposerValues>;
  context: DisplayRightsCompositionContextResponse;
  /** What the authorization covers, shown above the money. */
  scope?: MentionBoostAuthorizationScope;
  isSubmitting: boolean;
  onSubmit: () => void;
  /** Secondary way out: "skip" in the request flow, "back" in the contact card. */
  secondaryAction?: { label: string; onClick: () => void };
}

/**
 * The composition itself: terms, one message, one delivery channel, one send.
 *
 * The message is the same text on both sides — it is stored on the terms (so it
 * is hashed into the proof and shown on the creator's page) and inserted in the
 * email body. Nothing here may read as email-only.
 */
export function DisplayRightsComposerForm({
  form,
  context,
  scope,
  isSubmitting,
  onSubmit,
  secondaryAction,
}: DisplayRightsComposerFormProps) {
  const { t } = useTranslation();

  return (
    <form
      onSubmit={(event) => {
        event.preventDefault();
        onSubmit();
      }}
      data-testid="display-rights-composer-form"
    >
      <VStack align="stretch" gap="4">
        {scope && (
          // What the money buys, before the money. The scope is copied from the
          // authorization server-side and cannot be chosen here; showing it is
          // the difference between an informed commitment and a blind one.
          <Box
            borderWidth="1px"
            borderColor="border.DEFAULT"
            rounded="md"
            px="3"
            py="2"
            bg="whiteSmoke"
          >
            <Text
              fontSize="xs"
              color="fg.muted"
              lineHeight="tall"
              data-testid="display-rights-scope"
            >
              {t(`displayRights.composer.scope.${scope}`)}
            </Text>
          </Box>
        )}

        <DisplayRightsTermsFields control={form.control} currencyCode={context.currencyCode} />

        <Controller
          control={form.control}
          name="note"
          render={({ field, fieldState }) => (
            <Field
              label={t('displayRights.composer.messageLabel')}
              invalid={!!fieldState.error}
              errorText={fieldState.error ? t(fieldState.error.message ?? '') : undefined}
            >
              <Textarea
                {...field}
                variant="outline"
                rows={4}
                size="sm"
                data-testid="display-rights-note"
              />
            </Field>
          )}
        />

        <Controller
          control={form.control}
          name="channel"
          render={({ field }) => (
            <DisplayRightsChannelSection
              value={field.value}
              onChange={field.onChange}
              emailChannel={context.emailChannel}
            />
          )}
        />

        <VStack align="stretch" gap="2">
          <Button
            type="submit"
            width="full"
            colorPalette="primary"
            fontWeight="semibold"
            loading={isSubmitting}
            data-testid="display-rights-submit"
          >
            {t('displayRights.composer.submit')}
          </Button>
          {secondaryAction && (
            <Button
              type="button"
              width="full"
              variant="ghost"
              size="sm"
              onClick={secondaryAction.onClick}
              data-testid="display-rights-secondary"
            >
              {secondaryAction.label}
            </Button>
          )}
        </VStack>
      </VStack>
    </form>
  );
}
